import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import RedNeuronal2D from '../components/sala/RedNeuronal2D';
import EditorPensamiento from '../components/sala/EditorPensamiento';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileFeedIdeas from '../components/mobile/MobileFeedIdeas';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', moradoClaro:'#AFA9EC' };

export default function MisIdeasPage() {
  const { user, profile, membership } = useAuth();
  const { isMobile } = useScreen();
  const isMobileView = useIsMobile();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [sinapsis, setSinapsis] = useState([]);
  const [activa, setActiva] = useState(null);
  const [modoEditor, setModoEditor] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [dataReady, setDataReady] = useState(false);

  const isFree = !membership || membership === 'free_observer' || membership === 'tu_radar';

  const recargarSinapsis = () => {
    if (!user) return;
    return supabase.from('sinapsis').select('*').eq('user_from_id', user.id)
      .then(({ data }) => {
        setSinapsis((data || []).map(s => ({
          from_id: s.pensamiento_from_id || s.user_from_id,
          // In private mode, cluster to_id should point to hub 'td' (not user UUID)
          to_id: (s.tipo === 'cluster') ? 'td' : (s.pensamiento_to_id || s.user_to_id),
          tipo: s.tipo,
        })));
      });
  };

  useEffect(() => {
    if (!user) return;
    const cargar = async () => {
      const [ideasRes, sinRes] = await Promise.all([
        supabase.from('pensamientos').select('*').eq('user_id', user.id).eq('visibilidad', 'privado').order('created_at', { ascending: false }),
        supabase.from('sinapsis').select('*').eq('user_from_id', user.id),
      ]);
      const ideasData = ideasRes.data || [];
      setIdeas(ideasData);
      if (ideasData.length) setActiva(ideasData[0]);
      setSinapsis((sinRes.data || []).map(s => ({
        from_id: s.pensamiento_from_id || s.user_from_id,
        to_id: (s.tipo === 'cluster') ? 'td' : (s.pensamiento_to_id || s.user_to_id),
        tipo: s.tipo,
      })));
      setDataReady(true);
    };
    cargar();
  }, [user]);

  const publicarEnSala = async (idea) => {
    if (!idea || publicando) return;
    if (!window.confirm('¿Publicar esta idea como pensamiento en La Sala?\n\nSerá visible para toda la comunidad.')) return;
    setPublicando(true);
    const { data: nuevo, error } = await supabase.from('pensamientos').insert({
      user_id: user.id,
      titulo: idea.titulo,
      contenido: idea.contenido,
      tags: idea.tags,
      visibilidad: 'publico',
    }).select().single();

    if (error) { alert('Error: ' + error.message); setPublicando(false); return; }

    // Create cluster sinapsis for the new public thought
    await supabase.from('sinapsis').insert({
      pensamiento_from_id: nuevo.id, user_from_id: user.id, user_to_id: user.id, tipo: 'cluster', score: 1.0,
    });

    setPublicando(false);
    alert('¡Publicado en La Sala! Tu pensamiento ya es visible para todos.');
  };

  // Locked overlay for Free
  if (isFree) {
    return (
      <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ maxWidth:420, textAlign:'center' }}>
          <p style={{ fontSize:48, margin:'0 0 16px' }}>🧠</p>
          <h1 style={{ fontFamily:serif, fontSize:24, fontWeight:400, margin:'0 0 8px' }}>Mis Ideas</h1>
          <p style={{ fontSize:14, color:'rgba(255,255,255,0.5)', lineHeight:1.7, margin:'0 0 8px' }}>Tu red neuronal privada de ideas atómicas. Conecta tus pensamientos con wikilinks, madura ideas y publícalas cuando estén listas.</p>
          <p style={{ fontSize:13, color:C.moradoClaro, margin:'0 0 24px' }}>🔒 Disponible desde Doer</p>
          <button onClick={() => navigate('/membresias')} style={{ padding:'14px 32px', background:'linear-gradient(135deg, #534AB7, #1D9E75)', color:'#fff', border:'none', borderRadius:50, fontSize:15, fontWeight:500, cursor:'pointer', fontFamily:font, boxShadow:'0 0 30px rgba(93,193,185,0.3)' }}>
            Doer · $139/mes →
          </button>
          <button onClick={() => navigate('/sala')} style={{ display:'block', margin:'16px auto 0', background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:13, cursor:'pointer', fontFamily:font }}>
            ← Volver a La Sala
          </button>
        </div>
      </div>
    );
  }

  // Private mode: user IS the hub, no emprendedor nodes
  const emprendedores = [];
  const hubName = (profile?.full_name || 'Yo').split(' ')[0].slice(0, 8);

  if (isMobileView) {
    return (
      <MobileFeedIdeas
        ideas={ideas}
        user={user}
        profile={profile}
        membership={membership}
        onPublish={publicarEnSala}
        publicando={publicando}
        onIdeaSaved={(nueva, esEdicion) => {
          setIdeas(prev => esEdicion ? prev.map(i => i.id === nueva.id ? nueva : i) : [nueva, ...prev]);
          setTimeout(recargarSinapsis, 500);
        }}
      />
    );
  }

  return (
    <div style={{ background:'#0a0a0a', minHeight:'100vh', fontFamily:font, color:'#fff' }}>
      {/* Header */}
      <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
        <button onClick={() => navigate('/sala')} style={{ background:'rgba(83,74,183,0.25)', border:'0.5px solid rgba(175,169,236,0.5)', color:C.moradoClaro, padding:'5px 12px', borderRadius:7, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontWeight:500, fontFamily:'inherit' }}>
          ← La Sala
        </button>
        <span style={{ fontSize:12, color:C.moradoClaro }}>🧠 Mis Ideas · {ideas.length} ideas atómicas</span>
      </header>

      <div style={{ display:'flex', flexDirection:isMobile?'column':'row', minHeight:'calc(100vh - 50px)' }}>
        {/* Red neuronal privada */}
        <div style={{ flex:isMobile?'none':'1', padding:isMobile?'12px':'16px', borderBottom:isMobile?'0.5px solid rgba(255,255,255,0.06)':'none', borderRight:isMobile?'none':'0.5px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize:10, color:C.moradoClaro, letterSpacing:1.5, textTransform:'uppercase', margin:'0 0 8px' }}>Tu red privada</p>
          <div style={{ background:'radial-gradient(ellipse at center, rgba(83,74,183,0.12), transparent 70%)', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.05)', padding:8 }}>
            {!dataReady ? (
              <div style={{ height:300, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ width:24, height:24, border:'3px solid rgba(255,255,255,0.08)', borderTopColor:'#AFA9EC', borderRadius:'50%', animation:'tdSpin 0.8s linear infinite' }} />
                <style>{`@keyframes tdSpin{to{transform:rotate(360deg)}}`}</style>
              </div>
            ) : <RedNeuronal2D
              emprendedores={emprendedores}
              pensamientos={ideas}
              sinapsis={sinapsis.filter(s => ideas.some(i => i.id === s.from_id || i.id === s.to_id))}
              userId={user?.id}
              hubLabel={hubName}
              modoPrivado={true}
              onClickPensamiento={(id) => { setActiva(ideas.find(i => i.id === id) || null); setModoEditor(false); }}
              onClickEmprendedor={() => {}}
            />}
          </div>
        </div>

        {/* Sidebar + content */}
        <div style={{ flex:1, display:'flex', flexDirection:'column' }}>
          {/* Note list header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
            <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.5)' }}>Ideas atómicas · {ideas.length}</p>
            <button onClick={() => { setModoEditor(true); setActiva(null); }} style={{ width:28, height:28, background:'rgba(175,169,236,0.12)', border:'0.5px solid rgba(175,169,236,0.3)', borderRadius:6, color:C.moradoClaro, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
          </div>

          {/* Note list */}
          <div style={{ display:'flex', flexDirection:'column', gap:1, padding:'6px', maxHeight:isMobile?'30vh':'none', overflowY:'auto', borderBottom:'0.5px solid rgba(255,255,255,0.06)' }}>
            {ideas.map(idea => (
              <button key={idea.id} onClick={() => { setActiva(idea); setModoEditor(false); }} style={{
                padding:'8px 10px', background: activa?.id === idea.id ? 'rgba(83,74,183,0.18)' : 'transparent',
                border:0, borderLeft: activa?.id === idea.id ? '2px solid #AFA9EC' : '2px solid transparent',
                borderRadius: activa?.id === idea.id ? '0 6px 6px 0' : 6,
                textAlign:'left', cursor:'pointer', width:'100%', fontFamily:'inherit', color:'#fff',
              }}>
                <p style={{ margin:0, fontSize:12, fontWeight: activa?.id === idea.id ? 500 : 400, color: activa?.id === idea.id ? C.moradoClaro : 'rgba(255,255,255,0.7)' }}>{idea.titulo}</p>
                <p style={{ margin:'2px 0 0', fontSize:10, color:'rgba(255,255,255,0.35)' }}>{new Date(idea.created_at).toLocaleDateString('es-MX')}</p>
              </button>
            ))}
            {ideas.length === 0 && !modoEditor && (
              <p style={{ padding:'20px 10px', fontSize:12, color:'rgba(255,255,255,0.4)', textAlign:'center' }}>Todavía no tienes ideas. Haz clic en + para crear tu primera.</p>
            )}
          </div>

          {/* Content area */}
          <div style={{ flex:1, padding:isMobile?'12px':'16px 20px', overflowY:'auto' }}>
            {modoEditor ? (
              <EditorPensamiento
                key={activa?.id || 'new'}
                user={user}
                pensamientosUsuario={ideas}
                modo="idea_privada"
                isMobile={isMobile}
                pensamientoExistente={activa}
                onPublicar={(nueva) => {
                  setIdeas(prev => activa ? prev.map(i => i.id === nueva.id ? nueva : i) : [nueva, ...prev]);
                  setActiva(nueva);
                  setModoEditor(false);
                  setTimeout(recargarSinapsis, 500);
                }}
              />
            ) : activa ? (
              <article>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
                  <div>
                    <span style={{ display:'inline-block', padding:'2px 8px', background:'rgba(175,169,236,0.12)', border:'0.5px solid rgba(175,169,236,0.2)', borderRadius:50, fontSize:9, color:C.moradoClaro, marginBottom:6 }}>🧠 Idea atómica · Privada</span>
                    <h1 style={{ fontFamily:serif, fontSize:isMobile?20:24, fontWeight:400, margin:0 }}>{activa.titulo}</h1>
                    <p style={{ margin:'4px 0 0', fontSize:11, color:'rgba(255,255,255,0.4)' }}>{new Date(activa.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => setModoEditor(true)} style={{ padding:'7px 12px', background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:8, color:'rgba(255,255,255,0.6)', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                      ✏️ Editar
                    </button>
                    <button onClick={() => publicarEnSala(activa)} disabled={publicando} style={{ padding:'7px 12px', background:'rgba(93,193,185,0.12)', border:'0.5px solid rgba(93,193,185,0.3)', borderRadius:8, color:C.turquesa, fontSize:12, cursor:'pointer', fontFamily:'inherit', opacity:publicando?0.5:1 }}>
                      🚀 Publicar en La Sala
                    </button>
                    <button onClick={() => {
                      if (!window.confirm('¿Eliminar esta idea?')) return;
                      supabase.from('pensamientos').delete().eq('id', activa.id).then(({ error }) => {
                        if (error) { alert('Error'); return; }
                        setIdeas(prev => prev.filter(i => i.id !== activa.id));
                        setActiva(null);
                      });
                    }} style={{ padding:'7px 12px', background:'rgba(235,99,107,0.15)', border:'0.5px solid rgba(235,99,107,0.5)', borderRadius:8, color:C.coral, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                      🗑️
                    </button>
                  </div>
                </div>
                <div style={{ fontSize:15, color:'rgba(255,255,255,0.85)', lineHeight:1.8 }} dangerouslySetInnerHTML={{ __html: activa.contenido }} />
              </article>
            ) : (
              <div style={{ textAlign:'center', padding:'3rem 0' }}>
                <p style={{ fontSize:28, margin:'0 0 8px' }}>🧠</p>
                <p style={{ fontSize:14, color:'rgba(255,255,255,0.4)' }}>Selecciona una idea o crea una nueva</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`.wikilink { color:#AFA9EC !important; background:rgba(175,169,236,0.12); padding:1px 5px; border-radius:4px; text-decoration:underline dotted; font-weight:500; }`}</style>
    </div>
  );
}
